import React, { useMemo } from "react"

import { useForm } from "react-hook-form"

import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"
import type { IOVUpsertPayerMappingRequestData } from "@common-shared/types/requestData/oneVerifyRequestData.types"
import type { IPayer } from "@common-shared/types/responseData/commonResponseData.types"
import type { IOneVerifyPayerMapping } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { InputElement } from "@front-shared/elements/input.element"
import { PopupWrapperElement } from "@front-shared/elements/popupWrapper.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import type { IPopupProps } from "@front-shared/types/props.types"

import { RuntimeHelper } from "../../helpers/runtime.helper"
import { useHasPermissions } from "../../hooks/hasPermissions.hook"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import {
    payerMappingsCreate,
    payerMappingsUpdate,
} from "../../store/thunks/payerMappings.thunks"
import { PayerMappingsInfoNoticeComponent } from "../configurations/payerMappings/payerMappingsInfoNotice.component"
import { PayerMappingsPayerInputComponent } from "../configurations/payerMappings/payerMappingsPayerInput.component"

export const UpsertPayerMappingPopupComponent: React.FunctionComponent<
    IUpsertPayerMappingPopupComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const {
        handleSubmit,
        formState: { errors },
        control,
        setValue,
        register,
        unregister,
    } = useForm<IOVUpsertPayerMappingRequestData | any>()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)

    const canManagePracticeConfigurations = useHasPermissions(
        OneVerifyPermission.managePracticeConfigurations,
        undefined,
        false,
    )

    const isLoading = useMemo<boolean>(() => {
        return (
            RuntimeHelper.isPayerMappingsUpdateLoading() ||
            RuntimeHelper.isPayerMappingsCreateLoading() ||
            RuntimeHelper.isPayerMappingsGetListLoading()
        )
    }, [runtimeState.isLoading])

    const onSave = (
        formData: IOVUpsertPayerMappingRequestData & { payer: IPayer },
    ) => {
        if (!selectedPracticeRole || !canManagePracticeConfigurations) {
            return
        }

        const data = {
            externalPayerId: formData.externalPayerId.trim(),
            payerId: formData.payer.payerId.trim(),
            externalName: formData.externalName.trim(),
        }

        if (props.existingMapping) {
            dispatch(
                payerMappingsUpdate({
                    practice: selectedPracticeRole.practice,
                    payload: {
                        payerMappingId: props.existingMapping.id,
                        data,
                    },
                    onSuccess: props.onDone,
                }),
            )
        } else {
            dispatch(
                payerMappingsCreate({
                    payload: data,
                    practice: selectedPracticeRole?.practice,
                    onSuccess: props.onDone,
                }),
            )
        }
    }

    return (
        <PopupWrapperElement
            isActive={props.isActive}
            className={props.className}
            onDone={props.onClose}
            closeOnOutsideClick={props.closeOnOutsideClick}
            popupWidthClassName="w-[560px] max-w-[calc(100vw-32px)]"
        >
            <PrimaryText typography="h3">
                {props.existingMapping ? "Edit" : "Add"} Payer Mapping
            </PrimaryText>

            <form
                className="mt-[32px]"
                onSubmit={handleSubmit(onSave)}
                noValidate={true}
            >
                <InputElement
                    name="externalName"
                    label="Payer Name"
                    withRequiredStar={true}
                    reactHookControl={control}
                    reactHookSetValue={setValue}
                    reactHookErrors={errors}
                    reactHookValidations={{
                        required: FRONT_VALIDATION_CONFIG.required,
                    }}
                    defaultValue={props.existingMapping?.externalName}
                    disabled={isLoading}
                />

                <InputElement
                    className="mt-[32px]"
                    name="externalPayerId"
                    label="Payer ID"
                    withRequiredStar={true}
                    reactHookControl={control}
                    reactHookSetValue={setValue}
                    reactHookErrors={errors}
                    reactHookValidations={{
                        required: FRONT_VALIDATION_CONFIG.required,
                    }}
                    defaultValue={props.existingMapping?.externalPayerId}
                    disabled={isLoading}
                />

                <PayerMappingsPayerInputComponent
                    className="mt-[32px]"
                    defaultPayerId={props.existingMapping?.payerId}
                    reactHookFormRegister={register}
                    reactHookFormUnregister={unregister}
                    reactHookFormErrors={errors}
                    reactHookFormSet={setValue}
                    disabled={isLoading}
                />

                <PayerMappingsInfoNoticeComponent
                    className="mt-[24px]"
                    subject="Payer mapping"
                />

                <div className="mt-[24px] flex justify-end gap-[12px]">
                    <ButtonElement
                        label="Cancel"
                        type="default"
                        htmlType="button"
                        disabled={isLoading}
                        onClick={props.onClose}
                    />

                    <ButtonElement
                        label="Save"
                        type="primary"
                        htmlType="submit"
                        isLoading={isLoading}
                        disabled={isLoading}
                    />
                </div>
            </form>
        </PopupWrapperElement>
    )
}

interface IUpsertPayerMappingPopupComponentProps extends IPopupProps {
    existingMapping?: IOneVerifyPayerMapping
    onClose: () => void
}
